import React, { useEffect } from "react";
import "./styles.sass";

const formatAiSummaryText = (text) => {
  if (!text) return null;
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim().replace(/^[*•-]\s*/, ""))
    .filter(Boolean);

  return (
    <ul className="ai_summary_bullet_list">
      {lines.map((line, idx) => {
        const parts = line.split(/(\*\*.*?\*\*)/g);
        return (
          <li key={idx}>
            {parts.map((part, pIdx) => {
              if (part.startsWith("**") && part.endsWith("**")) {
                return <strong key={pIdx}>{part.slice(2, -2)}</strong>;
              }
              return part;
            })}
          </li>
        );
      })}
    </ul>
  );
};

export const StudentAiSummaryModal = ({
  open,
  onClose,
  data,
  loading = false,
  selectedTest = null,
  studentEmail = "",
  onViewQuestions,
  questionsLoading = false,
}) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const testName = selectedTest?.test_name || selectedTest?.chapter_name || selectedTest?.chapter || "Test";
  const roomCode = selectedTest?.room_code || "";
  const aiSummary = data?.ai_summary || "";
  const conceptData = Array.isArray(data?.concept_data) ? data.concept_data : [];

  return (
    <div className="student_ai_modal_overlay" onClick={onClose}>
      <div
        className="student_ai_modal_content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="student_ai_modal_header">
          <div className="student_ai_header_left">
            <div className="student_ai_title_row">
              <span className="ai_badge_icon">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7C3AED"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
                  <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
                  <path d="M12 3v18" />
                  <path d="M8 9h4" />
                  <path d="M12 14h4" />
                </svg>
              </span>
              <h2>AI Performance Analysis</h2>
              {testName && <span className="ai_test_tag">{testName}</span>}
              {roomCode && <span className="ai_room_tag">Room: {roomCode}</span>}
            </div>
            {studentEmail && (
              <p className="student_ai_email_subtitle">
                Student: <span>{studentEmail}</span>
              </p>
            )}
          </div>

          <button
            type="button"
            className="student_ai_close_btn"
            onClick={onClose}
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="student_ai_modal_body">
          {loading ? (
            <div className="student_ai_loading_state">
              <div className="ai_spinner"></div>
              <h4>Generating Student AI Performance Analysis...</h4>
              <p>Analyzing question timing, concept mastery, and accuracy patterns.</p>
            </div>
          ) : data?.status === "error" ? (
            <div className="student_ai_error_state">
              <p>{data.message || "Failed to load AI summary for this test."}</p>
            </div>
          ) : (
            <div className="student_ai_details_container">
              {/* AI Performance Summary Card */}
              {aiSummary && (
                <div className="student_ai_insights_card">
                  <div className="ai_card_header">
                    <span className="sparkle_icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6B21A8"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 3v18h18" />
                        <path d="m19 9-5 5-4-4-3 3" />
                      </svg>
                    </span>
                    <h3>AI Performance Insights</h3>
                  </div>
                  <div className="ai_card_body">
                    {formatAiSummaryText(aiSummary)}
                  </div>
                </div>
              )}

              {/* Concept Breakdown Table */}
              {conceptData.length > 0 && (
                <div className="student_ai_concepts_card">
                  <div className="concepts_card_header">
                    <h3>Concept Performance Breakdown</h3>
                    <span className="concepts_count_tag">
                      {conceptData.length} {conceptData.length === 1 ? "Concept" : "Concepts"}
                    </span>
                  </div>

                  <div className="concepts_table_wrapper">
                    <table className="student_ai_concept_table">
                      <thead>
                        <tr>
                          <th>Concept</th>
                          <th>Questions</th>
                          <th>Correct</th>
                          <th>Accuracy</th>
                          <th>Avg Time</th>
                          <th>Total Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conceptData.map((c, cIdx) => {
                          const accuracy =
                            c.total_questions > 0
                              ? Math.round((c.correct_answers / c.total_questions) * 100)
                              : 0;
                          return (
                            <tr key={cIdx}>
                              <td className="concept_title_col">{c.concept}</td>
                              <td>{c.total_questions}</td>
                              <td
                                style={{
                                  color: c.correct_answers > 0 ? "#059669" : "#DC2626",
                                  fontWeight: 700,
                                }}
                              >
                                {c.correct_answers}
                              </td>
                              <td>
                                <span
                                  className={`ai_acc_pill ${
                                    accuracy >= 70
                                      ? "high"
                                      : accuracy >= 40
                                      ? "mid"
                                      : "low"
                                  }`}
                                >
                                  {accuracy}%
                                </span>
                              </td>
                              <td>
                                <span className="ai_time_chip">
                                  ⏱ {c.average_time_seconds}s
                                </span>
                              </td>
                              <td>
                                <span className="ai_time_chip">
                                  {c.total_time_seconds}s
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!aiSummary && conceptData.length === 0 && (
                <div className="student_ai_empty_state">
                  <p>No AI analysis data available for this test attempt.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="student_ai_modal_footer">
          <button
            type="button"
            className="ai_modal_close_btn"
            onClick={onClose}
          >
            Close
          </button>

          <button
            type="button"
            className="ai_modal_view_more_btn"
            onClick={() => {
              if (onViewQuestions && selectedTest) {
                onViewQuestions(selectedTest);
              }
            }}
            disabled={questionsLoading}
          >
            <span>
              {questionsLoading ? "Loading Question Details..." : "View Question Details"}
            </span>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentAiSummaryModal;
